import { ChatSupportChannel, IChatConfiguration, IDeviceModel, IMCConnectionInfo } from '@nx-fe/services';
import { SABHA_MANUAL_PRESENCE_STATES, SabhaRoomType } from '@soti/sabha-components';
import { SabhaPresenceStatus } from '@soti/sabha-components/lib/presence/presence-status';
import { DEFAULT_SABHA_USER_PREF, SabhaUserPreference } from '../models/sabha-user-preference';
import { numericColorToHex } from '../utils';
import { SabhaActions, SabhaActionTypes } from './actions';

export enum SabhaInitState {
  None,
  Completed,
  Failed,
}

export interface SabhaState {
  sabhaInitState: SabhaInitState;
  showOnboarding: boolean;
  isSabhaAllowed: boolean;

  /**
   * Technician's user id on Sabha
   * platform
   */
  sabhaUserId: string;

  /**
   * Current presence status of the local
   * tecnician user on Sabah
   */
  localPresenceStatus: SabhaPresenceStatus;

  /**
   * All presences status available on Sabha
   * platform
   */
  allPresenceStatuses: SabhaPresenceStatus[];

  /**
   * Room which technician has a call established in
   */
  callRoomId: string;

  /**
   * Device which technician is RCing into
   */
  rcDevice: IDeviceModel;

  /**
   * Device which is being shown in minified view
   */
  minifiedViewDevice: IDeviceModel;

  /**
   * Ids of rooms currently showing one or more notifications
   */
  notificationRooms: string[];

  /**
   * Array of call ids for which we have outgoing
   * calls
   */
  rings: string[];

  /**
   * Map of room ids with activity time which are
   * soon to be expired and removed from the server
   */
  inactiveRoomIds: Map<string, number>;

  /**
   * UI state of the chat dialog
   */
  deviceChatDialog: DeviceChatDialogState;
  chatConfiguration: IChatConfiguration;
  errorView: ErrorViewState;
  sabhaUserPreference: SabhaUserPreference;
  deviceSearchText: string;

  /**
   * Array of MC connections
   */
  mcConnections: IMCConnectionInfo[];

  /**
   * Whether we are attempting to get or create room for a device
   */
  isStartDeviceChatLoading: boolean;

  /**
   * List of mat dialog refs currently open
   */
  dialogRefIds: string[];
}

export enum ErrorViewType {
  MicNotConnectedError = 'MicNotConnectedError',
  WebCamAndMicNotConnectedError = 'WebCamAndMicNotConnectedError',
  DeviceError = 'DeviceError',
  ParticipantBusyError = 'ParticipantBusyError',
  PermissionError = 'Permission',
  DeviceMissingError = 'DeviceMissingError',
  LicenseExpiredError = 'LicenseExpiredError',
}

export interface ErrorViewState {
  deviceId?: string;
  technicianName?: string;
  errorType: ErrorViewType;
  isVideoCall?: boolean;
  roomId?: string;
  permission?: string;
}

export interface DeviceChatDialogState {
  roomId: string;
  pendingActiveRoom: PendingRoomConfiguration;
  dialogSize: DeviceChatDialogSize;
  dialogPosition: DeviceChatDialogPosition;
  currentView: DeviceChatDialogView;
  startType?: SabhaRoomType;
  dialogSizeOnHide?: DeviceChatDialogSize;
  chatToggle: boolean;
}

export interface PendingRoomConfiguration {
  roomId: string;
  startType: SabhaRoomType;
}

export enum DeviceChatDialogView {
  MainView = 'MainView',
  ChatView = 'ChatView',
  CallView = 'CallView',
  ErrorView = 'ErrorView',
}

export enum DeviceChatDialogSize {
  Collapsed = 'Collapsed',
  FullSize = 'FullSize',
}

export interface DeviceChatDialogPosition {
  x: number;
  y: number;
}

export const initialDeviceChatDialogState: DeviceChatDialogState = {
  roomId: null,
  pendingActiveRoom: null,
  chatToggle: false,
  dialogSize: DeviceChatDialogSize.FullSize,
  dialogPosition: { x: 20, y: -20 },
  currentView: DeviceChatDialogView.MainView,
  startType: null,
  dialogSizeOnHide: DeviceChatDialogSize.Collapsed,
};

export const initialSabhaState: SabhaState = {
  allPresenceStatuses: SABHA_MANUAL_PRESENCE_STATES.map(
    state =>
    ({
      color: numericColorToHex(state.color),
      displayName: state.description,
      name: state.status,
    } as SabhaPresenceStatus)
  ),
  sabhaInitState: SabhaInitState.None,
  localPresenceStatus: undefined,
  sabhaUserId: null,
  isSabhaAllowed: undefined,
  showOnboarding: false,
  sabhaUserPreference: DEFAULT_SABHA_USER_PREF,
  rings: [],
  inactiveRoomIds: new Map(),
  notificationRooms: [],
  callRoomId: null,
  rcDevice: null,
  minifiedViewDevice: null,
  deviceChatDialog: initialDeviceChatDialogState,
  chatConfiguration: {
    supportChannel: ChatSupportChannel.Video,
    isEnabled: true,
  },
  errorView: {
    deviceId: undefined,
    technicianName: undefined,
    errorType: undefined,
    isVideoCall: undefined,
    roomId: undefined,
  },
  deviceSearchText: undefined,
  mcConnections: [],
  isStartDeviceChatLoading: false,
  dialogRefIds: [],
};

export function sabhaReducer(state: SabhaState = initialSabhaState, action: SabhaActions): SabhaState {
  switch (action.type) {
    case SabhaActionTypes.LOCAL_PRESENCE_STATUS_CHANGED:
      return {
        ...state,
        localPresenceStatus: action.status,
      };
    case SabhaActionTypes.SABHA_INIT_STATE_CHANGED:
      return {
        ...state,
        sabhaInitState: action.sabhaInitState,
      };
    case SabhaActionTypes.USER_LOAD_SUCCESS:
      return {
        ...state,
        sabhaUserId: action.sabhaUserId,
      };
    case SabhaActionTypes.LOAD_ONBOARDING_SETTINGS_SUCCESS:
      return {
        ...state,
        showOnboarding: action.showOnboarding,
      };
    case SabhaActionTypes.DISABLE_ONBOARDING:
      return {
        ...state,
        showOnboarding: false,
      };
    case SabhaActionTypes.SHOW_CHAT_NOTIFICATION_SUCCESS:
      return {
        ...state,
        notificationRooms: state.notificationRooms.concat([action.xSightRoomId]),
      };
    case SabhaActionTypes.HIDE_CHAT_NOTIFICATION:
      return {
        ...state,
        notificationRooms: state.notificationRooms.filter(roomId => roomId !== action.roomId),
      };
    case SabhaActionTypes.SHOW_CHAT_ERROR_VIEW:
      return {
        ...state,
        errorView: {
          deviceId: action.deviceId,
          technicianName: action.technicianName,
          errorType: action.errorType,
          isVideoCall: action.isVideoCallAllowed,
          roomId: action.roomId,
        },
        deviceChatDialog: {
          ...state.deviceChatDialog,
          currentView: DeviceChatDialogView.ErrorView,
          roomId: null,
        }
      };
    case SabhaActionTypes.SHOW_CHAT_PERMISSION_ERROR_VIEW:
      return {
        ...state,
        errorView: {
          errorType: ErrorViewType.PermissionError,
          deviceId: action.deviceId,
          permission: action.permission,
        },
      };
    case SabhaActionTypes.SELECT_CURRENT_VIEW:
      return {
        ...state,
        deviceChatDialog: {
          ...state.deviceChatDialog,
          currentView: action.currentView,
        },
      };
    case SabhaActionTypes.TOGGLE_CHAT:
      return {
        ...state,
        deviceChatDialog: {
          ...state.deviceChatDialog,
          chatToggle: action.chatToggle,
          dialogSize: !!action.size ? action.size : state.deviceChatDialog.dialogSize,
          dialogSizeOnHide: !action.chatToggle ? state.deviceChatDialog.dialogSize : null
        },
      };
    case SabhaActionTypes.RESIZE_CHAT_WINDOW:
      return {
        ...state,
        deviceChatDialog: {
          ...state.deviceChatDialog,
          dialogSize: action.dialogSize,
        },
      };
    case SabhaActionTypes.SELECT_ROOM:
      return {
        ...state,
        deviceChatDialog: {
          ...state.deviceChatDialog,
          roomId: action.roomId,
        },
      };
    case SabhaActionTypes.SET_ROOM_START_TYPE:
      return {
        ...state,
        deviceChatDialog: {
          ...state.deviceChatDialog,
          startType: action.startType,
        },
      };
    case SabhaActionTypes.RINGING_CALL:
      return {
        ...state,
        rings: state.rings.concat(action.roomId),
      };
    case SabhaActionTypes.CLEANUP_RINGING_CALL:
      return {
        ...state,
        rings: state.rings.filter(roomId => roomId !== action.roomId),
      };
    case SabhaActionTypes.START_ROOM_CALL_FAILED:
    case SabhaActionTypes.HANGUP_ROOM_CALL:
      return {
        ...state,
        callRoomId: null,
      };
    case SabhaActionTypes.RC_STARTED:
      return { ...state, rcDevice: action.mcDevice, minifiedViewDevice: action.mcDevice };
    case SabhaActionTypes.RC_ENDED:
      const newState = { ...state, rcDevice: null };
      if (state.rcDevice?.mcDeviceId === state.minifiedViewDevice?.mcDeviceId) {
        newState.minifiedViewDevice = null;
      }
      return newState;
    case SabhaActionTypes.SET_MINIFIED_VIEW_DEVICE:
      return { ...state, minifiedViewDevice: action.mcDevice };
    case SabhaActionTypes.REMOVE_MINIFIED_VIEW_DEVICE:
      return { ...state, minifiedViewDevice: null };
    case SabhaActionTypes.PENDING_SELECT_ROOM:
      return {
        ...state,
        deviceChatDialog: {
          ...state.deviceChatDialog,
          pendingActiveRoom: action.pendingActiveRoom,
        },
      };
    case SabhaActionTypes.REMOVE_PENDING_SELECT_ROOM:
      return {
        ...state,
        deviceChatDialog: {
          ...state.deviceChatDialog,
          pendingActiveRoom: null,
        },
      };
    case SabhaActionTypes.IS_SABHA_ALLOWED:
      return {
        ...state,
        isSabhaAllowed: action.isAllowed,
      };
    case SabhaActionTypes.CHAT_CONFIG_LOAD_SUCCESS:
      return {
        ...state,
        chatConfiguration: action.chatConfig,
      };
    case SabhaActionTypes.UPDATE_CHAT_WINDOW_POSITION:
      return {
        ...state,
        deviceChatDialog: {
          ...state.deviceChatDialog,
          dialogPosition: action.position,
        },
      };
    case SabhaActionTypes.UPDATE_ACTIVE_CALL_ROOM:
      return {
        ...state,
        callRoomId: action.roomId,
      };
    case SabhaActionTypes.ADD_INACTIVE_ROOM:
      return {
        ...state,
        inactiveRoomIds: new Map(
          [...state.inactiveRoomIds.entries(), ...Array<[string, number]>([action.roomId, action.expiresIn])].sort(
            (a, b) => a[1] - b[1]
          )
        ),
      };
    case SabhaActionTypes.REMOVE_INACTIVE_ROOM:
      return {
        ...state,
        inactiveRoomIds: new Map(
          [...state.inactiveRoomIds.entries()].filter(([roomId, _]) => roomId !== action.roomId)
        ),
      };
    case SabhaActionTypes.SET_SABHA_USER_PREFERENCE:
      return {
        ...state,
        sabhaUserPreference: action.sabhaUserPreference,
      };
    case SabhaActionTypes.SET_DEVICE_SEARCH_TEXT:
      return {
        ...state,
        deviceSearchText: action.deviceSearchText,
      };
    case SabhaActionTypes.MC_CONNECTIONS_LOAD_SUCCESS:
      return {
        ...state,
        mcConnections: action.mcConnections,
      };
    case SabhaActionTypes.START_CHAT_ROOM_LOADING:
      return {
        ...state,
        isStartDeviceChatLoading: action.isLoading,
      };
    case SabhaActionTypes.ADD_DIALOG:
      return {
        ...state,
        dialogRefIds: state.dialogRefIds.concat(action.dialogId),
      };
    case SabhaActionTypes.REMOVE_DIALOG:
      return {
        ...state,
        dialogRefIds: state.dialogRefIds.filter(dialogId => dialogId !== action.dialogId),
      };
    case SabhaActionTypes.CLOSE_ALL_DIALOGS_SUCCESS:
      return {
        ...state,
        dialogRefIds: [],
      };
    default:
      return state;
  }
}
